import { GzInfoOverlayWebComponent } from './GzInfoOverlayWebComponent';
import { OverlayConfig, OverlayEvents, OverlayPosition } from '../type';

export class GzOverlay {
  public static show(config: OverlayConfig, targetElement: HTMLElement): GzInfoOverlayWebComponent | undefined {
    const overlayBox = GzOverlay.buildOverlayBox(config);

    if (!overlayBox) {
      return;
    }

    overlayBox.addEventListener(OverlayEvents.OVERLAY_OPEN, () => {
      overlayBox.updateComplete.then(() => GzOverlay.updatePosition(overlayBox, targetElement));
    });

    overlayBox.open();
    return overlayBox;
  }

  private static buildOverlayBox(config: OverlayConfig): GzInfoOverlayWebComponent {
    const body = document.body;
    const overlayBox = new GzInfoOverlayWebComponent();

    for (const prop in config) {
      if (config.hasOwnProperty(prop)) {
        overlayBox[prop] = config[prop];
      }
      if (config.content) {
        overlayBox.appendChild(config.content);
      }
    }

    body.appendChild(overlayBox);

    return overlayBox;
  }

  private static getConstants(
    overlay: GzInfoOverlayWebComponent,
    targetElement: HTMLElement
  ):
    | {
        containerPadding: number;
        spaceFromTarget: number;
        arrowHalfWidth: number;
        arrowHalfHeight: number;
        overlayCoords: DOMRect;
        target: DOMRect;
        scrollLeft: number;
        scrollTop: number;
        targetHalfHeight: number;
        targetHalfWidth: number;
      }
    | undefined {
    if (!overlay.arrow) {
      return;
    }

    const scrollLeft = window.document.body.scrollLeft;
    const scrollTop = window.document.body.scrollTop;

    const containerPadding =
      (overlay.getBoundingClientRect().width - overlay.content.getBoundingClientRect().width) / 2;
    const spaceFromTarget = 4;
    const arrowHalfWidth = overlay.arrow.getBoundingClientRect().width / 2;
    const arrowHalfHeight = overlay.arrow.getBoundingClientRect().height / 2;
    const overlayCoords = overlay.getBoundingClientRect();
    const target = targetElement.getBoundingClientRect();
    const targetHalfHeight = target.height / 2;
    const targetHalfWidth = target.width / 2;

    return {
      containerPadding,
      spaceFromTarget,
      arrowHalfWidth,
      arrowHalfHeight,
      overlayCoords,
      target,
      scrollLeft,
      scrollTop,
      targetHalfHeight,
      targetHalfWidth,
    };
  }

  private static setPosition(
    overlay: GzInfoOverlayWebComponent,
    top: number,
    left: number,
    arrowPosition: OverlayPosition
  ) {
    if (overlay) {
      overlay.position = arrowPosition;
      overlay.style.top = top + 'px';
      overlay.style.left = left + 'px';
    }
  }

  public static updatePosition(overlay: GzInfoOverlayWebComponent | undefined, targetElement: HTMLElement) {
    if (!overlay) {
      return;
    }

    const constants = GzOverlay.getConstants(overlay, targetElement);

    if (!constants) {
      return;
    }
    const {
      containerPadding,
      spaceFromTarget,
      arrowHalfWidth,
      arrowHalfHeight,
      overlayCoords,
      target,
      scrollLeft,
      scrollTop,
      targetHalfHeight,
      targetHalfWidth,
    } = constants;

    const yTopLeft = target.top + target.height + arrowHalfHeight * 2 + spaceFromTarget + scrollTop;
    const xLeftTop = target.left + target.width + arrowHalfWidth * 2 + spaceFromTarget + scrollLeft;
    const xTopBottom = target.left - overlayCoords.width - arrowHalfWidth * 2 - spaceFromTarget + scrollLeft;
    const yBottomLeftRight = target.top - target.height - overlayCoords.height + scrollTop;

    switch (overlay.position) {
      case OverlayPosition.TOP_LEFT:
        const xTopLeft = target.left - containerPadding - arrowHalfWidth + targetHalfWidth + scrollLeft;
        GzOverlay.setPosition(overlay, yTopLeft, xTopLeft, OverlayPosition.TOP_LEFT);
        break;
      case OverlayPosition.TOP_RIGHT:
        const xTopRight =
          target.left - overlayCoords.width + containerPadding + arrowHalfWidth + targetHalfWidth + scrollLeft;
        GzOverlay.setPosition(overlay, yTopLeft, xTopRight, OverlayPosition.TOP_RIGHT);
        break;
      case OverlayPosition.LEFT_TOP:
        const yLeftTop = target.top - containerPadding - arrowHalfHeight + targetHalfHeight + scrollTop;
        // set the initial position of the overlay if it was not set yet
        if (overlayCoords.x === 0) {
          GzOverlay.setPosition(overlay, yLeftTop, xLeftTop, OverlayPosition.LEFT_TOP);
          break;
        }
        const newYLeftBottom =
          target.top - overlayCoords.height + containerPadding + targetHalfHeight + arrowHalfHeight + scrollTop;
        // update position in case overlay does not fit screen
        window.innerHeight > overlayCoords.bottom
          ? GzOverlay.setPosition(overlay, yLeftTop, xLeftTop, OverlayPosition.LEFT_TOP)
          : GzOverlay.setPosition(overlay, newYLeftBottom, xLeftTop, OverlayPosition.LEFT_BOTTOM);
        break;
      case OverlayPosition.LEFT_BOTTOM:
        const componentFitsInPage =
          targetElement.getBoundingClientRect().top > overlay.content.getBoundingClientRect().height;
        const yLeftBottom =
          target.top - overlayCoords.height + containerPadding + targetHalfHeight + arrowHalfHeight + scrollTop;
        const newYLeftTop = target.top - containerPadding - arrowHalfHeight + targetHalfHeight + scrollTop;
        componentFitsInPage
          ? GzOverlay.setPosition(overlay, yLeftBottom, xLeftTop, OverlayPosition.LEFT_BOTTOM)
          : GzOverlay.setPosition(overlay, newYLeftTop, xLeftTop, OverlayPosition.LEFT_TOP);
        break;
      case OverlayPosition.RIGHT_TOP:
        const yRightTop = target.top - arrowHalfHeight - containerPadding + targetHalfHeight + scrollTop;
        GzOverlay.setPosition(overlay, yRightTop, xTopBottom, OverlayPosition.RIGHT_TOP);
        break;
      case OverlayPosition.RIGHT_BOTTOM:
        const yRightBottom =
          target.top - overlayCoords.height + containerPadding + arrowHalfHeight + targetHalfHeight + scrollTop;
        GzOverlay.setPosition(overlay, yRightBottom, xTopBottom, OverlayPosition.RIGHT_BOTTOM);
        break;
      case OverlayPosition.BOTTOM_LEFT:
        const xBottomLeft = target.left - containerPadding - arrowHalfWidth + targetHalfWidth + scrollLeft;
        GzOverlay.setPosition(overlay, yBottomLeftRight, xBottomLeft, OverlayPosition.BOTTOM_LEFT);
        break;
      case OverlayPosition.BOTTOM_RIGHT:
        const xBottomRight =
          target.left - overlayCoords.width + containerPadding + arrowHalfWidth + targetHalfWidth + scrollLeft;
        GzOverlay.setPosition(overlay, yBottomLeftRight, xBottomRight, OverlayPosition.BOTTOM_RIGHT);
        break;
    }
  }

  static close(e: Event) {
    const overlayRef: GzInfoOverlayWebComponent | null = e
      .composedPath()
      .find((el: HTMLElement) => el.localName === 'gz-info-overlay') as GzInfoOverlayWebComponent;
    if (overlayRef) {
      overlayRef.close();
    }
  }
}
